import isEqual from 'lodash.isequal';

export type FormChangesResult =
  | { success: true; value: Record<string, unknown> }
  | { success: false; error: string; path: string[] };

const missing = Symbol('missing');

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function property(value: unknown, key: string): unknown {
  return isRecord(value) && Object.hasOwn(value, key) ? value[key] : missing;
}

/**
 * Serializer-generated additions are initialization intent, but only when the
 * raw definition has no value. Nulls and expression-valued ancestors are data.
 */
export function withoutSyntheticDefaults(
  raw: Record<string, unknown>,
  projection: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(Object.entries(projection)
    .filter(([key]) => Object.hasOwn(raw, key))
    .map(([key, value]) => [
      key,
      isRecord(raw[key]) && isRecord(value) ? withoutSyntheticDefaults(raw[key], value) : value,
    ]));
}

/**
 * Diff projections produced by the same serializer/metadata, applying only the
 * form's delta to live JSON. Explicit edits win; serializer-only initialization
 * fills missing values without overwriting data supplied by another writer.
 */
export function applyFormChanges(
  baseline: Record<string, unknown>,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  current: Record<string, unknown>,
  originalProjection: Record<string, unknown> = before,
): FormChangesResult {
  let failure: Extract<FormChangesResult, { success: false }> | undefined;
  const conflict = (path: string[]): typeof missing => {
    const field = path[0] === 'runAfter' ? 'Run After' : path.join('.') || 'the definition';
    failure ??= {
      success: false,
      path,
      error: `The form cannot safely update ${field} without losing data it does not represent. Reopen this node and use Code to edit this field.`,
    };
    return missing;
  };

  const merge = (
    original: unknown,
    oldProjection: unknown,
    newProjection: unknown,
    live: unknown,
    path: string[],
    unpruned: unknown,
  ): unknown => {
    if (isEqual(oldProjection, newProjection)) return live;
    if (isEqual(live, newProjection)) return live;
    if (isEqual(unpruned, newProjection) && live !== missing &&
      (!isRecord(live) || !isRecord(newProjection))) return live;

    const objectChange =
      (isRecord(oldProjection) || oldProjection === missing) &&
      (isRecord(newProjection) || newProjection === missing);
    if (objectChange) {
      const result = isRecord(live) ? { ...live } : {};
      const keys = new Set([
        ...Object.keys(isRecord(oldProjection) ? oldProjection : {}),
        ...Object.keys(isRecord(newProjection) ? newProjection : {}),
      ]);
      for (const key of keys) {
        const value = merge(
          property(original, key),
          property(oldProjection, key),
          property(newProjection, key),
          property(live, key),
          [...path, key],
          property(unpruned, key),
        );
        if (value === missing) {
          delete result[key];
        } else {
          Object.defineProperty(result, key, {
            value, enumerable: true, configurable: true, writable: true,
          });
        }
      }
      return newProjection === missing && Object.keys(result).length === 0 ? missing : result;
    }

    if (
      Array.isArray(original) && Array.isArray(oldProjection) &&
      (!isEqual(original, oldProjection) || !isEqual(original, live))
    ) {
      // A partial form or concurrent append must not truncate the live array.
      if (!Array.isArray(newProjection) || oldProjection.length !== newProjection.length) {
        return conflict(path);
      }
      if (!Array.isArray(live)) return conflict(path);
      const result = [...live];
      const used = new Set<number>();
      for (let index = 0; index < oldProjection.length; index += 1) {
        if (isEqual(oldProjection[index], newProjection[index])) continue;
        if (oldProjection.some((value, oldIndex) =>
          oldIndex !== index && isEqual(value, newProjection[index]),
        )) return conflict(path);
        const entry = oldProjection[index];
        if (original.filter((value) => isEqual(value, entry)).length !== 1 ||
          oldProjection.filter((value) => isEqual(value, entry)).length !== 1) {
          return conflict(path);
        }
        const matches = live.flatMap((value, liveIndex) =>
          !used.has(liveIndex) && isEqual(value, entry) ? [liveIndex] : [],
        );
        const originalIndex = original.findIndex((value) => isEqual(value, entry));
        const sameSlot = matches.length === 0 && live.length >= original.length &&
          original.every((value, rawIndex) => rawIndex === originalIndex || isEqual(value, live[rawIndex]));
        if (matches.length !== 1 && !sameSlot) return conflict(path);
        const match = matches[0] ?? originalIndex;
        if (used.has(match)) return conflict(path);
        used.add(match);
        result[match] = merge(entry, entry, newProjection[index], live[match], [...path, String(index)],
          Array.isArray(unpruned) ? unpruned[index] : missing);
      }
      return result;
    }
    return newProjection;
  };

  const value = merge(baseline, before, after, current, [], originalProjection);
  if (failure) return failure;
  return { success: true, value: value as Record<string, unknown> };
}
